import React, { useState, useEffect } from "react";
import { Link, useLocation, useParams } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import { Autocomplete, Box, Button, Container, MenuItem, TextField, Typography } from "@mui/material";
import { addressLookup } from "lib/helpers";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CheckIcon from "@mui/icons-material/Check";
import EnterpriseApplication from "models/EnterpriseApplication/EnterpriseApplication";
import { statesOperatedOptions } from "components/HomepageComponents/EnterpriseProSignupModal/dropdownOptions";
import enterpriseMessages from "rdx/modules/enterpriseApplications/messages";
import actions from "rdx/modules/platform/actions";
import { getRdxSelectionMapper } from "rdx/utils/propsMapping";
import StaticPageLayout from "../../components/StaticPageLayout";
import * as styles from "./styles";

type TranslateFnT = (key: string, options?: Record<string, unknown>) => string;

type OwnerT = {
  id: string;
  firstName: string;
  lastName: string;
  position: string;
  email: string;
  phone: string;
};

type FormDataT = {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  languagePreference: string;
  businessName: string;
  businessMailingAddress: string;
  city: string;
  state: string;
  zip: string;
  yearsInBusiness: string;
  statesOperatedIn: string[];
  numberOfSalesReps: string;
  avgMonthlySales: string;
  avgMonthlyRevenue: string;
  primaryLeadSource: string;
  businessModel: string;
};

type StepT = 1 | 2 | 3 | 4;
type ScreenT = "intro" | "form";

type LocationStateT = {
  prefill?: {
    firstName: string;
    lastName: string;
    email: string;
    phone: string;
    languagePreference: string;
  };
};

type StepIndicatorPropsT = {
  current: StepT;
  t: TranslateFnT;
};

const StepIndicator = ({ current, t }: StepIndicatorPropsT) => {
  const stepLabels = [
    t("steps.stepIndicator.info"),
    t("steps.stepIndicator.organization"),
    t("steps.stepIndicator.details"),
    t("steps.stepIndicator.owners"),
  ];

  return (
    <Box sx={styles.stepIndicatorContainer}>
      {stepLabels.map((label, idx) => {
        const stepNum = (idx + 1) as StepT;
        const isComplete = stepNum < current;
        const isActive = stepNum === current;

        return (
          <React.Fragment key={label}>
            <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 0.75 }}>
              <Box sx={styles.stepCircle(isComplete, isActive)}>
                {isComplete ? (
                  <CheckIcon sx={{ color: "#040922", fontSize: 18, fontWeight: 700 }} />
                ) : (
                  <Typography sx={styles.stepNumber(isActive)}>{stepNum}</Typography>
                )}
              </Box>
              <Typography sx={styles.stepLabel(isComplete, isActive)}>{label}</Typography>
            </Box>
            {idx < stepLabels.length - 1 && <Box sx={styles.stepConnector(idx + 1 < current)} />}
          </React.Fragment>
        );
      })}
    </Box>
  );
};

type IntroScreenPropsT = {
  onApply: () => void;
  t: TranslateFnT;
};

const IntroScreen = ({ onApply, t }: IntroScreenPropsT) => (
  <Box sx={{ textAlign: "center" }}>
    <Typography sx={styles.eyebrow}>{t("intro.eyebrow")}</Typography>
    <Typography variant="h2" sx={styles.introHeading}>
      {t("intro.heading")}
    </Typography>
    <Typography sx={styles.introPrice}>
      $350
      <Box component="span" sx={{ fontSize: "1.4rem", fontWeight: 600 }}>
        {t("intro.priceMonth")}
      </Box>
    </Typography>
    <Button fullWidth variant="contained" onClick={onApply} sx={styles.ctaButton}>
      {t("intro.applyNow")}
    </Button>
    <Typography sx={styles.introDisclaimer}>{t("intro.disclaimer")}</Typography>
  </Box>
);

type Step1PropsT = {
  formData: FormDataT;
  onChange: (field: keyof FormDataT, value: string) => void;
  onNext: () => void;
  readOnly?: boolean;
  t: TranslateFnT;
};

const Step1 = ({ formData, onChange, onNext, readOnly, t }: Step1PropsT) => (
  <Box
    component="form"
    onSubmit={(e: React.FormEvent) => {
      e.preventDefault();
      onNext();
    }}
  >
    <Typography sx={styles.eyebrow}>{t("form.step1.eyebrow")}</Typography>
    <Typography variant="h3" sx={styles.stepHeading}>
      {t("form.step1.heading")}
    </Typography>
    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2.5 }}>
      <Box sx={{ width: { xs: "100%", sm: "calc(50% - 10px)" } }}>
        <TextField
          fullWidth
          required
          label={t("form.labels.firstName")}
          value={formData.firstName}
          onChange={(e) => onChange("firstName", e.target.value)}
          sx={styles.darkInput}
          disabled={readOnly}
        />
      </Box>
      <Box sx={{ width: { xs: "100%", sm: "calc(50% - 10px)" } }}>
        <TextField
          fullWidth
          required
          label={t("form.labels.lastName")}
          value={formData.lastName}
          onChange={(e) => onChange("lastName", e.target.value)}
          sx={styles.darkInput}
          disabled={readOnly}
        />
      </Box>
      <Box sx={{ width: { xs: "100%", sm: "calc(50% - 10px)" } }}>
        <TextField
          fullWidth
          required
          label={t("form.labels.email")}
          type="email"
          value={formData.email}
          onChange={(e) => onChange("email", e.target.value)}
          sx={styles.darkInput}
          disabled={readOnly}
        />
      </Box>
      <Box sx={{ width: { xs: "100%", sm: "calc(50% - 10px)" } }}>
        <TextField
          fullWidth
          required
          label={t("form.labels.phone")}
          value={formData.phone}
          onChange={(e) => onChange("phone", e.target.value)}
          sx={styles.darkInput}
          disabled={readOnly}
        />
      </Box>
      <Box sx={{ width: "100%" }}>
        <TextField
          fullWidth
          required
          select
          label={t("form.labels.languagePreference")}
          value={formData.languagePreference}
          onChange={(e) => onChange("languagePreference", e.target.value)}
          sx={styles.darkInput}
          SelectProps={{ MenuProps: styles.selectMenuProps }}
          disabled={readOnly}
        >
          <MenuItem value="en">{t("form.languageOptions.english")}</MenuItem>
          <MenuItem value="es">{t("form.languageOptions.spanish")}</MenuItem>
        </TextField>
      </Box>
      <Box sx={{ width: "100%" }}>
        <Button fullWidth variant="contained" type="submit" sx={styles.ctaButton}>
          {t("form.cta.continue")}
        </Button>
      </Box>
    </Box>
  </Box>
);

type Step2PropsT = {
  formData: FormDataT;
  onChange: (field: keyof FormDataT, value: string) => void;
  onNext: () => void;
  onBack: () => void;
  t: TranslateFnT;
};

type AddressSuggestionT = {
  street_line: string;
  city: string;
  state: string;
  zipcode: string;
};

const Step2 = ({ formData, onChange, onNext, onBack, t }: Step2PropsT) => {
  const [suggestions, setSuggestions] = useState<AddressSuggestionT[]>([]);

  const handleAddressSearch = async (value: string) => {
    const results = await addressLookup(value);
    setSuggestions(results || []);
  };

  const handleAddressSelect = (_: React.SyntheticEvent, suggestion: AddressSuggestionT | string | null) => {
    if (!suggestion || typeof suggestion === "string") return;
    onChange("businessMailingAddress", suggestion.street_line);
    onChange("city", suggestion.city);
    onChange("state", suggestion.state);
    onChange("zip", suggestion.zipcode);
  };

  return (
    <Box
      component="form"
      onSubmit={(e: React.FormEvent) => {
        e.preventDefault();
        onNext();
      }}
    >
      <Typography sx={styles.eyebrow}>{t("form.step2.eyebrow")}</Typography>
      <Typography variant="h3" sx={styles.stepHeading}>
        {t("form.step2.heading")}
      </Typography>
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2.5 }}>
        <Box sx={{ width: "100%" }}>
          <TextField
            fullWidth
            label={t("form.labels.businessName")}
            value={formData.businessName}
            onChange={(e) => onChange("businessName", e.target.value)}
            sx={styles.darkInput}
          />
        </Box>
        <Box sx={{ width: "100%" }}>
          <Autocomplete
            freeSolo
            options={suggestions}
            getOptionLabel={(option) =>
              typeof option === "string"
                ? option
                : `${option.street_line} ${option.city}, ${option.state} ${option.zipcode}`
            }
            inputValue={formData.businessMailingAddress}
            onInputChange={(_, value) => {
              onChange("businessMailingAddress", value);
              handleAddressSearch(value);
            }}
            onChange={handleAddressSelect}
            componentsProps={{
              paper: {
                sx: {
                  background: "#0b1120",
                  border: "1px solid rgba(255,255,255,0.12)",
                  "& .MuiAutocomplete-option": {
                    fontFamily: "'Barlow', sans-serif",
                    color: "rgba(255,255,255,0.82)",
                    "&:hover": { background: "rgba(81,242,203,0.1)" },
                    "&[aria-selected='true']": {
                      background: "rgba(81,242,203,0.15)",
                      color: "#51F2CB",
                      "&:hover": { background: "rgba(81,242,203,0.2)" },
                    },
                  },
                },
              },
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                fullWidth
                required
                label={t("form.labels.businessMailingAddress")}
                sx={styles.darkInput}
              />
            )}
          />
        </Box>
        <Box sx={{ width: { xs: "100%", sm: "calc(41.667% - 13.333px)" } }}>
          <TextField
            fullWidth
            label={t("form.labels.city")}
            value={formData.city}
            onChange={(e) => onChange("city", e.target.value)}
            sx={styles.darkInput}
          />
        </Box>
        <Box sx={{ width: { xs: "100%", sm: "calc(33.333% - 13.333px)" } }}>
          <TextField
            fullWidth
            label={t("form.labels.state")}
            value={formData.state}
            onChange={(e) => onChange("state", e.target.value)}
            sx={styles.darkInput}
          />
        </Box>
        <Box sx={{ width: { xs: "100%", sm: "calc(25% - 13.333px)" } }}>
          <TextField
            fullWidth
            label={t("form.labels.zip")}
            value={formData.zip}
            onChange={(e) => onChange("zip", e.target.value)}
            sx={styles.darkInput}
          />
        </Box>
        <Box sx={{ width: "100%" }}>
          <Button fullWidth variant="contained" type="submit" sx={styles.ctaButton}>
            {t("form.cta.continue")}
          </Button>
        </Box>
        <Box sx={{ width: "100%", textAlign: "center" }}>
          <Button onClick={onBack} sx={styles.backButton}>
            {t("form.cta.back")}
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

type Step3PropsT = {
  formData: FormDataT;
  onChange: (field: keyof FormDataT, value: string | string[]) => void;
  onNext: () => void;
  onBack: () => void;
  t: TranslateFnT;
};

const Step3 = ({ formData, onChange, onNext, onBack, t }: Step3PropsT) => (
  <Box
    component="form"
    onSubmit={(e: React.FormEvent) => {
      e.preventDefault();
      onNext();
    }}
  >
    <Typography sx={styles.eyebrow}>{t("form.step3.eyebrow")}</Typography>
    <Typography variant="h3" sx={styles.stepHeading}>
      {t("form.step3.heading")}
    </Typography>
    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2.5 }}>
      <Box sx={{ width: { xs: "100%", sm: "calc(50% - 10px)" } }}>
        <TextField
          fullWidth
          required
          label={t("form.labels.yearsInBusiness")}
          value={formData.yearsInBusiness}
          onChange={(e) => onChange("yearsInBusiness", e.target.value)}
          sx={styles.darkInput}
        />
      </Box>
      <Box sx={{ width: { xs: "100%", sm: "calc(50% - 10px)" } }}>
        <TextField
          fullWidth
          required
          select
          label={t("form.labels.statesOperatedIn")}
          value={formData.statesOperatedIn}
          onChange={(e) => onChange("statesOperatedIn", e.target.value as unknown as string[])}
          sx={styles.darkInput}
          SelectProps={{
            multiple: true,
            MenuProps: styles.selectMenuProps,
            renderValue: (selected) =>
              (selected as string[])
                .map((v) => {
                  const match = statesOperatedOptions.find((o) => o.value === v);
                  return match ? match.label : v;
                })
                .join(", "),
          }}
        >
          {statesOperatedOptions.map((option) => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </TextField>
      </Box>
      <Box sx={{ width: { xs: "100%", sm: "calc(50% - 10px)" } }}>
        <TextField
          fullWidth
          required
          label={t("form.labels.numberOfSalesReps")}
          value={formData.numberOfSalesReps}
          onChange={(e) => onChange("numberOfSalesReps", e.target.value)}
          sx={styles.darkInput}
        />
      </Box>
      <Box sx={{ width: { xs: "100%", sm: "calc(50% - 10px)" } }}>
        <TextField
          fullWidth
          required
          label={t("form.labels.avgMonthlySales")}
          value={formData.avgMonthlySales}
          onChange={(e) => onChange("avgMonthlySales", e.target.value)}
          sx={styles.darkInput}
        />
      </Box>
      <Box sx={{ width: "100%" }}>
        <TextField
          fullWidth
          required
          label={t("form.labels.avgMonthlyRevenue")}
          value={formData.avgMonthlyRevenue}
          onChange={(e) => onChange("avgMonthlyRevenue", e.target.value)}
          sx={styles.darkInput}
        />
      </Box>
      <Box sx={{ width: "100%" }}>
        <TextField
          fullWidth
          required
          select
          label={t("form.labels.primaryLeadSource")}
          value={formData.primaryLeadSource}
          onChange={(e) => onChange("primaryLeadSource", e.target.value)}
          sx={styles.darkInput}
          SelectProps={{ MenuProps: styles.selectMenuProps }}
        >
          <MenuItem value="Ambassador">{t("form.leadSourceOptions.ambassador")}</MenuItem>
          <MenuItem value="Friend Referral">{t("form.leadSourceOptions.friendReferral")}</MenuItem>
          <MenuItem value="Online Marketing">{t("form.leadSourceOptions.onlineMarketing")}</MenuItem>
          <MenuItem value="Word of Mouth">{t("form.leadSourceOptions.wordOfMouth")}</MenuItem>
          <MenuItem value="Door Knocking">{t("form.leadSourceOptions.doorKnocking")}</MenuItem>
          <MenuItem value="Facebook">{t("form.leadSourceOptions.facebook")}</MenuItem>
          <MenuItem value="YouTube">{t("form.leadSourceOptions.youtube")}</MenuItem>
          <MenuItem value="Other">{t("form.leadSourceOptions.other")}</MenuItem>
        </TextField>
      </Box>
      <Box sx={{ width: "100%" }}>
        <Button fullWidth variant="contained" type="submit" sx={styles.ctaButton}>
          {t("form.cta.continue")}
        </Button>
      </Box>
      <Box sx={{ width: "100%", textAlign: "center" }}>
        <Button onClick={onBack} sx={styles.backButton}>
          {t("form.cta.back")}
        </Button>
      </Box>
    </Box>
  </Box>
);

type Step4PropsT = {
  formData: FormDataT;
  owners: OwnerT[];
  onChange: (field: keyof FormDataT, value: string) => void;
  onOwnerChange: (index: number, field: keyof OwnerT, value: string) => void;
  onAddOwner: () => void;
  onRemoveOwner: (index: number) => void;
  onSubmit: () => void;
  onBack: () => void;
  t: TranslateFnT;
};

const Step4 = ({
  formData,
  owners,
  onChange,
  onOwnerChange,
  onAddOwner,
  onRemoveOwner,
  onSubmit,
  onBack,
  t,
}: Step4PropsT) => (
  <Box
    component="form"
    onSubmit={(e: React.FormEvent) => {
      e.preventDefault();
      onSubmit();
    }}
  >
    <Typography sx={styles.eyebrow}>{t("form.step4.eyebrow")}</Typography>
    <Typography variant="h3" sx={styles.stepHeading}>
      {t("form.step4.heading")}
    </Typography>
    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2.5 }}>
      <Box sx={{ width: "100%" }}>
        <TextField
          fullWidth
          required
          multiline
          rows={4}
          label={t("form.labels.businessModel")}
          helperText={t("form.helperText.businessModel")}
          value={formData.businessModel}
          onChange={(e) => onChange("businessModel", e.target.value)}
          sx={styles.businessModelInput}
        />
      </Box>
    </Box>

    <Box sx={{ mt: 4, mb: 2 }}>
      <Typography sx={styles.primaryOwnersTitle}>{t("form.owners.title")}</Typography>
      <Typography sx={styles.primaryOwnersSubtitle}>{t("form.owners.subtitle")}</Typography>

      {owners.map((owner, index) => (
        <Box key={owner.id} sx={styles.ownerCard}>
          <Box
            sx={{
              position: "absolute",
              top: 0,
              ...(index % 2 === 0 ? { left: 0 } : { right: 0 }),
              width: "60%",
              height: "60%",
              background:
                index % 2 === 0
                  ? "radial-gradient(ellipse at top left, rgba(81,242,203,0.12) 0%, transparent 70%)"
                  : "radial-gradient(ellipse at top right, rgba(32,194,241,0.11) 0%, transparent 70%)",
              pointerEvents: "none",
            }}
          />
          {owners.length > 1 && (
            <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 1 }}>
              <Button onClick={() => onRemoveOwner(index)} sx={styles.removeOwnerButton}>
                {t("form.owners.remove")}
              </Button>
            </Box>
          )}
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2 }}>
            <Box sx={{ width: { xs: "100%", sm: "calc(50% - 8px)" } }}>
              <TextField
                fullWidth
                required
                label={t("form.labels.firstName")}
                value={owner.firstName}
                onChange={(e) => onOwnerChange(index, "firstName", e.target.value)}
                sx={styles.darkInput}
              />
            </Box>
            <Box sx={{ width: { xs: "100%", sm: "calc(50% - 8px)" } }}>
              <TextField
                fullWidth
                required
                label={t("form.labels.lastName")}
                value={owner.lastName}
                onChange={(e) => onOwnerChange(index, "lastName", e.target.value)}
                sx={styles.darkInput}
              />
            </Box>
            <Box sx={{ width: { xs: "100%", sm: "calc(50% - 8px)" } }}>
              <TextField
                fullWidth
                required
                label={t("form.labels.positionHeld")}
                value={owner.position}
                onChange={(e) => onOwnerChange(index, "position", e.target.value)}
                sx={styles.darkInput}
              />
            </Box>
            <Box sx={{ width: { xs: "100%", sm: "calc(50% - 8px)" } }}>
              <TextField
                fullWidth
                required
                label={t("form.labels.email")}
                type="email"
                value={owner.email}
                onChange={(e) => onOwnerChange(index, "email", e.target.value)}
                sx={styles.darkInput}
              />
            </Box>
            <Box sx={{ width: "100%" }}>
              <TextField
                fullWidth
                required
                label={t("form.labels.phone")}
                value={owner.phone}
                onChange={(e) => onOwnerChange(index, "phone", e.target.value)}
                sx={styles.darkInput}
              />
            </Box>
          </Box>
        </Box>
      ))}

      <Button onClick={onAddOwner} sx={styles.addOwnerButton}>
        {t("form.owners.addAdditional")}
      </Button>
    </Box>

    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2.5 }}>
      <Box sx={{ width: "100%" }}>
        <Button fullWidth variant="contained" type="submit" sx={styles.ctaButton}>
          {t("form.cta.submitApplication")}
        </Button>
      </Box>
      <Box sx={{ width: "100%", textAlign: "center" }}>
        <Button onClick={onBack} sx={styles.backButton}>
          {t("form.cta.back")}
        </Button>
      </Box>
    </Box>
  </Box>
);

type SuccessStatePropsT = {
  email: string;
  t: TranslateFnT;
};

const SuccessState = ({ email, t }: SuccessStatePropsT) => {
  const { user } = useParams<{ user?: string }>();
  const prefix = user ? `/${user}` : "";
  return (
    <Box sx={{ textAlign: "center" }}>
      <CheckCircleIcon sx={{ color: "#51F2CB", fontSize: 80, mb: 2 }} />
      <Typography variant="h4" sx={styles.successHeading}>
        {t("success.heading")}
      </Typography>
      <Typography sx={styles.successSubtitle}>{t("success.subtitle")}</Typography>
      <Typography sx={styles.successBody}>{t("success.body", { email })}</Typography>
      <Button component={Link} to={`${prefix}/enterprise`} variant="contained" sx={styles.ctaButton}>
        {t("success.backToEnterprise")}
      </Button>
    </Box>
  );
};

const initialFormData: FormDataT = {
  firstName: "",
  lastName: "",
  email: "",
  phone: "",
  languagePreference: "",
  businessName: "",
  businessMailingAddress: "",
  city: "",
  state: "",
  zip: "",
  yearsInBusiness: "",
  statesOperatedIn: [],
  numberOfSalesReps: "",
  avgMonthlySales: "",
  avgMonthlyRevenue: "",
  primaryLeadSource: "",
  businessModel: "",
};

let ownerIdCounter = 0;
const emptyOwner = (): OwnerT => ({
  id: String(++ownerIdCounter),
  firstName: "",
  lastName: "",
  position: "",
  email: "",
  phone: "",
});

const selectProps = getRdxSelectionMapper({
  pageReferrer: "selectPageReferrer",
  applicationId: "selectEnterpriseApplicationDetailId",
  latestMessage: "getLatestMessageEvt",
});

const EnterpriseSignupContent = () => {
  const { t } = useTranslation("static_enterprise_signup");
  const { user } = useParams<{ user?: string }>();
  const location = useLocation<LocationStateT>();
  const prefill = location.state?.prefill;
  const prefix = user ? `/${user}` : "";
  const [screen, setScreen] = useState<ScreenT>(prefill ? "form" : "intro");
  const [step, setStep] = useState<StepT>(1);
  const [complete, setComplete] = useState(false);
  const [formData, setFormData] = useState<FormDataT>(prefill ? { ...initialFormData, ...prefill } : initialFormData);
  const [owners, setOwners] = useState<OwnerT[]>([emptyOwner()]);
  const dispatch = useDispatch();
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const { pageReferrer, applicationId, latestMessage } = useSelector(selectProps) as any;

  useEffect(() => {
    if (applicationId) {
      setComplete(true);
      window.scrollTo(0, 0);
    }
  }, [applicationId]);

  useEffect(() => {
    if (latestMessage?.message === enterpriseMessages.ERROR_CREATING_ENTERPRISE_APPLICATIONS) {
      let msg;
      if (latestMessage.message) {
        if (latestMessage.error?.status >= 500) {
          msg = `${latestMessage.message}: something went wrong.`;
        } else {
          msg = `${latestMessage.message}: ${latestMessage.error?.message || "something is wrong with your request."}`;
        }
      } else {
        msg = "Error: something went wrong.";
      }
      dispatch(actions.setAlertMessageVisible({ message: msg, severity: "error" }));
    }
  }, [latestMessage, dispatch]);

  const handleChange = (field: keyof FormDataT, value: string | string[]) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleOwnerChange = (index: number, field: keyof OwnerT, value: string) => {
    setOwners((prev) => prev.map((owner, i) => (i === index ? { ...owner, [field]: value } : owner)));
  };

  const handleAddOwner = () => {
    setOwners((prev) => [...prev, emptyOwner()]);
  };

  const handleRemoveOwner = (index: number) => {
    setOwners((prev) => prev.filter((_, i) => i !== index));
  };

  const handleNext = () => {
    if (step < 4) setStep((step + 1) as StepT);
  };

  const handleBack = () => {
    if (step > 1) setStep((step - 1) as StepT);
  };

  const handleSubmit = () => {
    const params = {
      ownerFirstName: formData.firstName,
      ownerLastName: formData.lastName,
      ownerEmail: formData.email,
      ownerPhone: formData.phone,
      locale: formData.languagePreference,
      spokenLanguages: formData.languagePreference ? [formData.languagePreference] : [],
      businessName: formData.businessName,
      businessStreet: formData.businessMailingAddress,
      businessCity: formData.city,
      businessState: formData.state,
      businessZip: formData.zip,
      yearsInBusiness: formData.yearsInBusiness,
      statesOperatedIn: formData.statesOperatedIn,
      numberOfReps: formData.numberOfSalesReps,
      monthlySales: formData.avgMonthlySales,
      monthlyRevenue: formData.avgMonthlyRevenue,
      primaryLeadSource: formData.primaryLeadSource,
      sellerValue: formData.businessModel,
      backgroundChecks: owners.map((o) => ({
        first_name: o.firstName,
        last_name: o.lastName,
        position_held: o.position,
        email: o.email,
        phone: o.phone,
      })),
      ...(pageReferrer?.vanityName && { vanityName: pageReferrer.vanityName }),
    };
    EnterpriseApplication.create(params);
  };

  const renderContent = () => {
    if (complete) {
      return (
        <Box key="success" sx={{ animation: "fadeUp 0.5s ease both" }}>
          <SuccessState email={formData.email} t={t} />
        </Box>
      );
    }
    if (screen === "intro") {
      return (
        <Box key="intro" sx={{ animation: "fadeUp 0.45s ease both" }}>
          <IntroScreen onApply={() => setScreen("form")} t={t} />
        </Box>
      );
    }
    return (
      <Box key={step} sx={{ animation: "fadeUp 0.45s ease both" }}>
        {step === 1 && (
          <Step1 formData={formData} onChange={handleChange} onNext={handleNext} readOnly={!!prefill} t={t} />
        )}
        {step === 2 && (
          <Step2 formData={formData} onChange={handleChange} onNext={handleNext} onBack={handleBack} t={t} />
        )}
        {step === 3 && (
          <Step3 formData={formData} onChange={handleChange} onNext={handleNext} onBack={handleBack} t={t} />
        )}
        {step === 4 && (
          <Step4
            formData={formData}
            owners={owners}
            onChange={handleChange}
            onOwnerChange={handleOwnerChange}
            onAddOwner={handleAddOwner}
            onRemoveOwner={handleRemoveOwner}
            onSubmit={handleSubmit}
            onBack={handleBack}
            t={t}
          />
        )}
      </Box>
    );
  };

  return (
    <Box sx={styles.pageWrapper}>
      <Box sx={styles.backgroundOverlay} />

      <Container maxWidth="sm" sx={styles.containerWrapper(screen === "intro" || complete)}>
        {screen === "form" && !complete && <StepIndicator current={step} t={t} />}

        <Box sx={styles.formCard}>
          <Box
            sx={{
              position: "absolute",
              top: 0,
              left: 0,
              width: "55%",
              height: "55%",
              background: "radial-gradient(ellipse at top left, rgba(81,242,203,0.13) 0%, transparent 70%)",
              pointerEvents: "none",
            }}
          />
          <Box
            sx={{
              position: "absolute",
              bottom: 0,
              right: 0,
              width: "50%",
              height: "50%",
              background: "radial-gradient(ellipse at bottom right, rgba(32,194,241,0.10) 0%, transparent 70%)",
              pointerEvents: "none",
            }}
          />
          {renderContent()}
        </Box>

        <Box sx={{ mt: 3, display: "flex", justifyContent: "center" }}>
          <Button
            component={Link}
            to={`${prefix}/enterprise`}
            startIcon={<ArrowBackIcon />}
            sx={styles.backToEnterpriseButton}
          >
            {t("nav.backToEnterprise")}
          </Button>
        </Box>
      </Container>
    </Box>
  );
};

const EnterpriseSignupPage = () => {
  const { t } = useTranslation("static_enterprise_signup");
  return (
    <StaticPageLayout title={t("meta.title")}>
      <EnterpriseSignupContent />
    </StaticPageLayout>
  );
};

export default EnterpriseSignupPage;
